import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { geoLevelFilterType, sectorLevelFilterType, showBlockChangesColumn } from 'venn-state';
import { useSectorHoldingsTreeGrid } from './useSectorHoldingsTreeGrid';
import { GeoLevelFilterType, SectorLevelFilterType } from '../components/grid/types';
import type { HoldingsDataProps } from '../types';
import { isNil } from 'lodash';
import { getChangesColumnDef } from './gridStyling';

export const useHoldingsTreeGrid = ({
  selectedRefId,
  isBarChart = false,
}: Pick<HoldingsDataProps, 'selectedRefId' | 'isBarChart'>) => {
  const geoLevelFilter = useRecoilValue(geoLevelFilterType(selectedRefId));
  const sectorLevelFilter = useRecoilValue(sectorLevelFilterType(selectedRefId));
  const showChangesColumn = useRecoilValue(showBlockChangesColumn(selectedRefId));

  const {
    rowData: rawRowData,
    columnDefs: originalColumnDefs,
    autoGroupColumnDef,
  } = useSectorHoldingsTreeGrid({
    selectedRefId,
    isBarChart,
  });

  const rowData = useMemo(
    () =>
      rawRowData
        .map((row) => {
          if (!row.isMetadata && row.value?.length === 2) {
            if (isNil(row.value[0]) || isNil(row.value[1])) return row;

            return {
              ...row,
              changes: row.value[0] - row.value[1],
            };
          }
          return row;
        })
        .filter((row) => {
          const noFiltersPresent = !geoLevelFilter && !sectorLevelFilter;
          if (row.isMetadata || noFiltersPresent) {
            return true;
          }

          switch (geoLevelFilter) {
            case GeoLevelFilterType.REGION:
              return row.path.length === 1;
            case GeoLevelFilterType.SUB_REGION:
              return row.path.length <= 2;
            case GeoLevelFilterType.DEFAULT:
              return true;
          }

          switch (sectorLevelFilter) {
            case SectorLevelFilterType.TOP_LEVEL:
              return row.path.length === 1;
            case SectorLevelFilterType.SUB_SECTOR:
              return row.path.length <= 2;
            case SectorLevelFilterType.DEFAULT:
              return true;
          }

          return true;
        }),
    [geoLevelFilter, sectorLevelFilter, rawRowData],
  );

  const columnDefs = useMemo(() => {
    if (!showChangesColumn) return originalColumnDefs;

    return [...originalColumnDefs, getChangesColumnDef()];
  }, [originalColumnDefs, showChangesColumn]);

  return {
    rowData,
    columnDefs,
    autoGroupColumnDef,
  };
};
